import React, { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Popover,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import { MoreIcon, SearchIcon } from "assets/icons";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "shared/config/configureStore";
import { useAppSelector, useDebounce } from "shared/lib/hooks/hooks";
import { deleteProductByProductId } from "api/thunk/products/deleteActions/deleteProductByProductId";
import SecondConfirmModal from "shared/ui/SecondConfirmModal/SecondConfirmModal";
import trashImg from "assets/images/admin/trash_black.svg";
import { getProducts } from "api/thunk/products/getActions/getProducts";
import editIcon from "assets/images/admin/editVoucher.svg";
import { useNavigate } from "react-router-dom";
import { UPDATE_PRODUCT } from "pages/paths";
import { setProductsPage, setProductsPageSize } from "api/slice/productsSlice/productsSlice";
import {
  Input,
} from "entities/admin/lib/MUIStyles/MUIStyles";
import { getMarathonsLookup } from "api/thunk/marathons/getActions/getMarathonsLookup";
import { InfinitySelect } from "shared/ui/InfinitySelect/InfinitySelect";
import { selectStyle } from "shared/lib/style";
import { setAllMarathonsLookup, setMarathonLookupName, setMarathonsLookupId, setMarathonsLookupPage } from "api/slice/marathonsSlice/marathonsSlice";
import { getProductsCategories } from "api/thunk/productCategories/getActions/getProductsCategories";
import { setAllProductsCategories, setProductCategoryId, setProductsCategoriesPage, setProductsCategoriesSearchTerm } from "api/slice/productCategoriesSlice/productCategoriesSlice";
import { getProductsCategoriesForInfinitySelect } from "api/thunk/productCategories/getActions/getProductsCategoriesForInfinitySelect";
interface IProductsTabProps {
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
}

interface IOption {
  label: string;
  value: string;
}


const ProductsTab: FC<IProductsTabProps> = ({
  search,
  setSearch
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: products, loading: productsLoading } = useAppSelector((state) => state.products.products.response);
  const marathonsLookup = useAppSelector((state) => state.marathons.marathonsLookup);
  const marathonsLookupId = useAppSelector((state) => state.marathons.marathonsLookupId);
  const marathonsLookupName = useAppSelector((state) => state.marathons.marathonsLookup.params.name);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);

  const [marathonsPageSize, setMarathonsPageSize] = useState(10);
  const [marathonsHasMore, setMarathonsHasMore] = useState(true);
  const debouncedMarathonsSearch = useDebounce(marathonsLookupName, 600);


  const { data: productCategories, loading: productCategoriesLoading } = useAppSelector(
    (state) => state.productsCategories.productsCategoriesForInfinitySelect.response
  );

  const productCategoryId = useAppSelector((state) => state.productsCategories.productCategoryId);
  const { searchTerm: productCategoriesSearchTerm } = useAppSelector(
    (state) => state.productsCategories.productsCategoriesForInfinitySelect.params
  );

  const [productCategoriesPageSize, setProductCategoriesPageSize] = useState(10);
  const [productCategoriesHasMore, setProductCategoriesHasMore] = useState(true);
  const debouncedProductCategoriesSearch = useDebounce(productCategoriesSearchTerm, 600);

  const loadMoreProductCategories = async () => {
    if (productCategoriesLoading || !productCategoriesHasMore) return;

    try {
      const nextPage = productCategoriesPageSize + 10;

      const res = await dispatch(
        getProductsCategories({
          page: 1,
          pageSize: nextPage,
          searchTerm: debouncedProductCategoriesSearch ?? undefined,
        })
      ).unwrap();

      setProductCategoriesPageSize(nextPage);
      setProductCategoriesHasMore(nextPage < (res.totalCount ?? 0));
    } catch (e) {
      console.error(e);
    }
  };

  const productCategoriesOptions: IOption[] =
    (productCategories?.items ?? []).map((c: any) => ({
      label: String(c?.name),
      value: String(c?.id),
    }));

  const selectedProductCategory: IOption | null =
    productCategoriesOptions.find((o) => Number(o.value) === Number(productCategoryId)) ??
    (productCategoryId ? { value: String(productCategoryId), label: productCategoriesSearchTerm ?? "" } : null);

  useEffect(() => {
    dispatch(
      getProductsCategoriesForInfinitySelect({
        page: 1,
        pageSize: productCategoriesPageSize,
        searchTerm: debouncedProductCategoriesSearch ?? undefined,
      })
    );
  }, [i18n.language, debouncedProductCategoriesSearch]);

  const { page: productsPage, pageSize: productsPageSize } = useAppSelector(
    (state) => state.products.products.params
  );

  const page = productsPage ?? 1;
  const pageSize = productsPageSize ?? 10;

  const handlePopoverOpen = (event: React.MouseEvent<HTMLButtonElement>, productId: number) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setPopoverPosition({ top: rect.bottom, left: rect.right });
    setPopoverOpen(true);
    setSelectedProductId(productId);
  };

  const handlePopoverClose = () => {
    setPopoverOpen(false);
    setPopoverPosition(null);
  };

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
    handlePopoverClose();
  };

  const handleConfirmDelete = async () => {
    if (selectedProductId !== null) {
      try {
        const response = await dispatch(
          deleteProductByProductId(selectedProductId)
        );

        if (response.meta.requestStatus === "fulfilled") {
          dispatch(getProducts({}));
        }
      } catch (error) {
        console.error(error);
      }
    }

    setDeleteModalOpen(false);
    setSelectedProductId(null);
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
  };

  const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#ECF2F7",
      color: "#3E444A",
      padding: "8px 16px",
      fontFamily: "Raleway",
      fontSize: "12px",
      lineHeight: "20px",
      border: "none",
      fontWeight: "700 !important",
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  const StyledTableRow = styled(TableRow)(() => ({
    "&:nth-of-type(odd)": {
      backgroundColor: "#F5FAFF",
    },
    "&:last-child td, &:last-child th": {
      border: 0,
    }
  }));

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    dispatch(setProductsPage(value))
  }

  const handlePageSizeChange = (e: SelectChangeEvent<number>) => {
    const newSize = Number(e.target.value);

    dispatch(setProductsPageSize(newSize));
    dispatch(setProductsPage(1));
    dispatch(getProducts({ page: 1, pageSize: newSize }));
  };

  const loadMoreMarathons = async () => {
    if (marathonsLookup.response.loading || !marathonsHasMore) return;

    try {
      const nextPage = marathonsPageSize + 10;
      const res = await dispatch(
        getMarathonsLookup({
          page: 1,
          pageSize: nextPage,
          searchTerm: debouncedMarathonsSearch ?? undefined,
        })
      ).unwrap();

      setMarathonsPageSize(nextPage);
      setMarathonsHasMore(nextPage < (res.totalCount ?? 0));
    } finally { }
  };

  const marathonsOptions: IOption[] =
    (marathonsLookup.response.data?.items ?? []).map((m: any) => ({ label: String(m?.name), value: String(m?.id) }));

  const selectedMarathon: IOption | null =
    marathonsOptions.find((o) => Number(o.value) === Number(marathonsLookupId)) ??
    (marathonsLookupId ? { value: String(marathonsLookupId), label: marathonsLookupName ?? "" } : null);


  useEffect(() => {
    dispatch(getMarathonsLookup({
      page: 1,
      pageSize: marathonsPageSize,
      searchTerm: debouncedMarathonsSearch ?? undefined
    }));
  }, [i18n.language, debouncedMarathonsSearch]);

  return (
    <Box
      sx={{
        margin: "0 auto",
        width: "100%",
        background: "#FFFFFF",
        borderRadius: "16px",
        marginTop: "24px",
        padding: "24px",
      }}
    >
      <Box>
        <Box sx={{
          display: "flex",
          gap: "10px",
          width: "100%"
        }}>
          <TextField
            type="search"
            variant="outlined"
            size="small"
            sx={{
              ...Input,
              minWidth: '500px',
              width: "50%",
              marginBottom: "24px",
              '& .MuiOutlinedInput-root': {
                padding: "1.5px 13px 1.5px 0",
                '&:hover fieldset': {
                  borderColor: '#0090D1',
                },
              },
            }}
            placeholder={"Поиск"}
            value={search}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={(e) => setSearch(e.target.value)}
          />
          <InfinitySelect
            options={marathonsOptions}
            value={selectedMarathon}
            inputValue={marathonsLookupName ?? ""}
            onInputChange={(e) => {
              dispatch(setMarathonLookupName(e?.target?.value ?? ""));
              dispatch(setMarathonsLookupPage(1));
              dispatch(setAllMarathonsLookup([]));
              setMarathonsHasMore(true);
            }}
            onChange={(val) => { val ? dispatch(setMarathonsLookupId(val.value)) && dispatch(setMarathonLookupName(val.label)) : dispatch(setMarathonsLookupId(null)) && dispatch(setMarathonLookupName("")); }}
            loadMore={loadMoreMarathons}
            hasMore={marathonsHasMore}
            loading={marathonsLookup.response.loading}
            placeholder="Выберите марафон"
            sx={{
              ...Input,
              "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
                fontSize: '16px',
                fontWeight: 400,
                padding: '0 !important',
                height: '22px',
                width: '35%',
                selectStyle,
              },
              "& fieldset": {
                borderRadius: "10px",
                border: "1px solid #C0D4E3",
              },
            }}
          />
          <InfinitySelect
            options={productCategoriesOptions}
            value={selectedProductCategory}
            inputValue={productCategoriesSearchTerm ?? ""}
            onInputChange={(e) => {
              dispatch(setProductsCategoriesSearchTerm(e?.target?.value ?? ""));
              setProductsCategoriesPage(1);
              dispatch(setAllProductsCategories([]));
              setProductCategoriesHasMore(true);
            }}
            onChange={(val) => { val ? dispatch(setProductCategoryId(val.value)) && dispatch(setProductsCategoriesSearchTerm(val.label)) : dispatch(setProductCategoryId(null)) && dispatch(setProductsCategoriesSearchTerm("")); }}
            loadMore={loadMoreProductCategories}
            hasMore={productCategoriesHasMore}
            loading={productCategoriesLoading}
            placeholder="Выберите категорию продукта"
            sx={{
              ...Input,
              "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
                fontSize: '16px',
                fontWeight: 400,
                padding: '0 !important',
                height: '22px',
                width: '35%',
                selectStyle,
              },
              "& fieldset": {
                borderRadius: "10px",
                border: "1px solid #C0D4E3",
              },
            }}
          />
        </Box>
        <TableContainer component={Paper} sx={{ boxShadow: "none", borderRadius: "12px 12px 0 0" }}>
          <Table sx={{ minWidth: 700 }} aria-label="customized table">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell>№</StyledTableCell>
                <StyledTableCell>{t("tables.name")}</StyledTableCell>
                <StyledTableCell>{t("tables.description")}</StyledTableCell>
                <StyledTableCell>{t("tables.price")}</StyledTableCell>
                <StyledTableCell>{t("tables.stock")}</StyledTableCell>
                <StyledTableCell>{t("tables.category")}</StyledTableCell>
                <StyledTableCell>{t("tables.marathon")}</StyledTableCell>
                <StyledTableCell align="center">{t("myMarathon.actions")}</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {productsLoading ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box display="flex" justifyContent="center" py={4}>
                      <CircularProgress size={24} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : products?.items?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      py={4}
                      color="#9e9e9e"
                      fontSize="14px"
                    >
                      No data
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                products?.items?.map((product, index) => (
                  <StyledTableRow key={product?.id}>
                    <StyledTableCell>{(page - 1) * pageSize + index + 1}</StyledTableCell>
                    <StyledTableCell>{product?.name}</StyledTableCell>
                    <StyledTableCell>{product?.description}</StyledTableCell>
                    <StyledTableCell>
                      {product?.price} {t("myMarathon.som")}
                    </StyledTableCell>
                    <StyledTableCell>
                      {product?.available} {t("registerToMarathon.pieces")}
                    </StyledTableCell>
                    <StyledTableCell>
                      {product?.categoryName}
                    </StyledTableCell>
                    <StyledTableCell>
                      {product?.marathonName}
                    </StyledTableCell>
                    <StyledTableCell align="center" sx={{ width: 30, p: 0 }}>
                      <IconButton size="small" onClick={(e) => handlePopoverOpen(e, product?.id)}>
                        <MoreIcon />
                      </IconButton>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack
          spacing={2}
          sx={{
            margin: "30px auto 20px auto",
            width: "500px",
            alignItems: "center",
            "@media (max-width: 515px)": {
              width: "400px",
            },
            "@media (max-width: 420px)": {
              width: "300px",
            },
          }}
        >
          <Pagination
            count={Math.ceil((products?.totalCount || 0) / pageSize)}
            page={productsPage ?? 1}
            onChange={handlePageChange}
            color="primary"
            size="medium"
            variant="outlined"
            shape="rounded"
            sx={{ margin: "0 auto" }}
          />
          <>
            <Select
              value={productsPageSize}
              name="pageSize"
              onChange={handlePageSizeChange}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: "12px",
                    padding: "0 10px",
                    "& .MuiMenuItem-root:hover": {
                      backgroundColor: "#e3f2fd",
                    },
                  },
                },
              }}
              sx={{
                borderRadius: "12px",
                border: "1px solid #C0D4E3",
                width: "100px",
                margin: "0 auto",
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                ".MuiSelect-select": {
                  padding: "10px 32px 10px 12px",
                  color: "#3E444A",
                  fontFamily: "Raleway",
                  fontSize: "14px",
                  lineHeight: "24px",
                },
              }}
            >
              {[10, 20, 30, 40, 50].map((value) => (
                <MenuItem
                  key={value}
                  value={value}
                  sx={{ borderRadius: "10px" }}
                >
                  {value}
                </MenuItem>
              ))}
            </Select>
            <InputLabel>{t("activeMarathonListOfParticipants.quantity")}</InputLabel>
          </>
        </Stack>
      </Box>

      {
        popoverPosition && (
          <Popover
            open={popoverOpen}
            onClose={handlePopoverClose}
            anchorReference="anchorPosition"
            anchorPosition={{
              top: popoverPosition.top,
              left: popoverPosition.left,
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                borderRadius: "12px",
                boxShadow: "0px 4px 20px rgba(0,0,0,0.08)",
                minWidth: 160,
                p: "4px",
              },
            }}
          >
            <Box display="flex" flexDirection="column">
              <Button
                fullWidth
                size="small"
                onClick={() =>
                  navigate(
                    `/admin_panel/${UPDATE_PRODUCT(selectedProductId)}`
                  )
                }
                startIcon={
                  <Box
                    component="img"
                    src={editIcon}
                    alt="Edit"
                    sx={{
                      width: 18,
                      height: 18,
                      filter:
                        "invert(39%) sepia(99%) saturate(749%) hue-rotate(166deg) brightness(95%) contrast(101%)",
                    }}
                  />
                }
                sx={{
                  justifyContent: "flex-start",
                  textTransform: "none",
                  fontWeight: 500,
                  px: 2,
                  py: 1,
                  borderRadius: "8px",
                  color: "#0090D1",
                  "&:hover": {
                    backgroundColor: "#F4F6F8",
                  },
                }}
              >
                {t("buttons.edit")}
              </Button>

              <Button
                fullWidth
                size="small"
                onClick={handleDeleteClick}
                startIcon={
                  <Box
                    component="img"
                    src={trashImg}
                    alt="Delete"
                    sx={{
                      width: 18,
                      height: 18,
                      filter:
                        "invert(53%) sepia(92%) saturate(3125%) hue-rotate(336deg) brightness(104%) contrast(97%)",
                    }}
                  />
                }
                sx={{
                  justifyContent: "flex-start",
                  textTransform: "none",
                  fontWeight: 500,
                  px: 2,
                  py: 1,
                  borderRadius: "8px",
                  color: "#FF6960",
                  "&:hover": {
                    backgroundColor: "rgba(255, 105, 96, 0.08)",
                  },
                }}
              >
                {t("buttons.delete")}
              </Button>
            </Box>
          </Popover>
        )
      }
      <SecondConfirmModal
        open={deleteModalOpen}
        status="error"
        title={t("confirmModal.deleteProductConfirmation")}
        description={t("confirmModal.deleteProductText")}
        confirmText={t("buttons.delete")}
        cancelText={t("buttons.cancel")}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </Box >
  );
};

export default ProductsTab;

